import { httpClient } from '@/lib/httpClient';

export type VisionMonitorOperator =
  | 'contains'
  | 'equals'
  | 'notEquals'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'eq'
  | 'gte'
  | 'lte'
  | 'is'
  | 'in'
  | 'notIn';

export interface VisionMonitorFilter {
  field: string;
  operator: VisionMonitorOperator;
  value?: string | string[];
}

export interface VisionMonitorScope {
  customerId?: number;
  createdFrom?: string;
  createdTo?: string;
  releaseFrom?: string;
  releaseTo?: string;
  filters?: VisionMonitorFilter[];
}

export interface VisionMonitorQuery extends VisionMonitorScope {
  page: number;
  limit: number;
}

export type VisionBlocker =
  | 'OUT_OF_STOCK'
  | 'INACTIVE'
  | 'NO_NAME'
  | 'NO_IMAGE'
  | 'CATEGORY_NOT_READY'
  | 'CUSTOMER_CATEGORY_UPDATE_OFF'
  | 'CUSTOMER_NO_TAXONOMY'
  | 'CUSTOMER_AUTO_RUN_OFF';

export interface VisionMonitorRow {
  catalogId: number;
  productId: string;
  sku: string | null;
  productName: string | null;
  creationDate: string;
  releaseDate: string | null;
  visionCategory: string | null;
  visionCategoryStatus: string;
  visionCategoryError: string | null;
  visionCategoryAttempts: number;
  needVisionRun: boolean;
  sentToVision: boolean;
  visionRun: boolean;
  visionStatus: string;
  visionError: string | null;
  visionAttempts: number;
  visionReadCount: number;
  visionFirstReadAt: string | null;
  visionLastReadAt: string | null;
  stock: boolean | null;
  isActive: boolean | null;
  seoStatus: string;
  seoSynced: boolean;
  seoError: string | null;
  customerId: number | null;
  customerName: string | null;
  categoryBlockers: VisionBlocker[];
  visionBlockers: VisionBlocker[];
}

export interface VisionMonitorProductsResponse {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  items: VisionMonitorRow[];
}

export interface VisionMonitorMetrics {
  total: number;
  category: { pending: number; success: number; error: number };
  vision: { pending: number; success: number; error: number; synced: number };
  flags: { needVisionRun: number; sentToVision: number; visionRun: number };
  catalog: {
    inStock: number;
    outOfStock: number;
    active: number;
    inactive: number;
  };
  seo: { pending: number; success: number; error: number; synced: number };
  pipeline: {
    waitingCategory: number;
    queuedForVision: number;
    inVision: number;
    blockedByStockOrActive: number;
  };
}

function toParams(scope: VisionMonitorScope) {
  const { filters, ...rest } = scope;

  return {
    ...rest,
    filters: filters?.length ? JSON.stringify(filters) : undefined,
  };
}

class VisionMonitorService {
  async getProducts(query: VisionMonitorQuery) {
    const { page, limit, ...scope } = query;

    const { data } = await httpClient.get<VisionMonitorProductsResponse>(
      '/v1/vision-monitor/products',
      { params: { page, limit, ...toParams(scope) } },
    );

    return data;
  }

  async getMetrics(scope: VisionMonitorScope) {
    const { data } = await httpClient.get<VisionMonitorMetrics>(
      '/v1/vision-monitor/metrics',
      { params: toParams(scope) },
    );

    return data;
  }

  // Forces the product(s) back through the Vision reading pipeline.
  async rereadProducts(catalogIds: number[]) {
    const { data } = await httpClient.post(
      '/v1/products/reset-vision/products',
      { catalogIds },
    );

    return data;
  }

  // Sends the product(s) back to the Vision category name classification.
  async regenerateCategory(catalogIds: number[]) {
    const { data } = await httpClient.post(
      '/v1/products/reset-vision/category',
      { catalogIds },
    );

    return data;
  }
}

export default new VisionMonitorService();
